import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useUserState } from '../../hooks/context/useUserState';
import useRoutes from '../../hooks/useRoutes';
import { useCfgParam } from '../../hooks/useCfgParam';
import { getLocalStorageKey } from '../../hooks/useLocalStorageKey';
import {
  postGuideProgress,
  requestGuide,
  requestGuideIndex,
  requestGuideProgress,
  requestPath,
  requestTour,
} from '../../api/requests/guides/requestGuides';
import {
  GuideDetail,
  GuideIndex,
  GuideProgressKind,
  GuideProgressStatus,
  PathDefinition,
  TourAction,
  TourDefinition,
  TourMode,
} from '../../types/guides';
import { guideIdForPathname } from '../../utils/guides/guideKeys';
import { navigationForGuideTarget } from '../../utils/guides/guideInlineActions';
import { useModal } from '../../hooks/useModal';
import Modal from '../../components/Modals/Modal/Modal';
import GuideNavigationModalContent from '../../components/Guide/GuideNavigationModalContent/GuideNavigationModalContent';
import {
  EMPTY_GUIDE_PANEL_HISTORY,
  GuidePanelHistory,
  guidePanelHistoryStorageKey,
  moveGuidePanelHistoryBack,
  moveGuidePanelHistoryForward,
  nextGuideId,
  previousGuideId,
  pushGuidePanelHistory,
  readGuidePanelHistory,
  writeGuidePanelHistory,
} from '../../utils/guides/guidePanelHistory';
import {
  applyGuideProgress,
  getGuideProgress,
  GuideProgressMap,
  guideProgressStorageKey,
  mergeGuideProgress,
  readLocalGuideProgress,
  writeLocalGuideProgress,
} from '../../utils/guides/guideProgressStore';
import { GlobalLocationStateType } from '../../types/types';

// AIR-410 Průvodce MySeptim: one context for the agenda help panel (layer B) and the
// step-by-step walkthroughs (layer C). Content comes from backend definitions; this
// context only loads, caches and tracks progress. Nothing here blocks the app —
// every request failure degrades to "no help available".

export interface ActiveTour {
  tour: TourDefinition;
  stepIndex: number;
  mode: TourMode;
}

export interface GuideFormNavigationGuard {
  hasUnsavedChanges: boolean;
  save: (afterSuccess: () => void) => void;
}

export interface GuideContextValue {
  isProvided: boolean;
  index: GuideIndex | null;
  indexLoading: boolean;
  indexError: boolean;
  ensureIndex: () => Promise<GuideIndex | null>;
  loadGuide: (guideId: string) => Promise<GuideDetail | null>;
  loadTour: (tourId: string) => Promise<TourDefinition | null>;
  loadPath: (pathId: string) => Promise<PathDefinition | null>;
  /** Guide id of the screen the user is on (dataset / form / app), or null. */
  currentScreenGuideId: string | null;
  panelGuideId: string | null;
  openPanel: (guideId: string) => void;
  openPanelAtScreen: (guideId: string) => void;
  closePanel: () => void;
  panelCanGoBack: boolean;
  panelCanGoForward: boolean;
  goBackPanel: () => void;
  goForwardPanel: () => void;
  activeTour: ActiveTour | null;
  startTour: (tourId: string, mode?: TourMode) => Promise<void>;
  nextStep: () => void;
  prevStep: () => void;
  stopTour: () => void;
  toggleMode: () => void;
  progress: GuideProgressMap;
  markProgress: (
    kind: GuideProgressKind,
    itemId: string,
    status: GuideProgressStatus,
    step?: number | null
  ) => void;
  /** Performs a tour navigation action; false when the route is unknown or deferred. */
  navigate: (action: TourAction, afterNavigation?: () => void) => boolean;
  /** Navigates to an internal URL while protecting unsaved form changes. */
  navigateToUrl: (
    url: string,
    afterNavigation?: () => void,
    state?: GlobalLocationStateType
  ) => boolean;
  registerFormNavigationGuard: (guard: GuideFormNavigationGuard) => () => void;
}

const noop = () => undefined;

const defaultValue: GuideContextValue = {
  isProvided: false,
  index: null,
  indexLoading: false,
  indexError: false,
  ensureIndex: async () => null,
  loadGuide: async () => null,
  loadTour: async () => null,
  loadPath: async () => null,
  currentScreenGuideId: null,
  panelGuideId: null,
  openPanel: noop,
  openPanelAtScreen: noop,
  closePanel: noop,
  panelCanGoBack: false,
  panelCanGoForward: false,
  goBackPanel: noop,
  goForwardPanel: noop,
  activeTour: null,
  startTour: async () => undefined,
  nextStep: noop,
  prevStep: noop,
  stopTour: noop,
  toggleMode: noop,
  progress: {},
  markProgress: noop,
  navigate: () => false,
  navigateToUrl: () => false,
  registerFormNavigationGuard: () => noop,
};

export const GuideContext = createContext<GuideContextValue>(defaultValue);

// Safe everywhere: components rendered outside the provider (tests, logged-out pages)
// get inert no-ops instead of a thrown error.
export const useGuideContext = (): GuideContextValue =>
  useContext(GuideContext);

const GuideContextProvider = ({ children }: { children: ReactNode }) => {
  const {
    state: { token, userLogin },
  } = useUserState();
  const history = useHistory();
  const location = useLocation();
  const routes = useRoutes();
  const subUrl = useCfgParam('subUrl');
  const localStorageKey = getLocalStorageKey(subUrl);
  const storageKey = guideProgressStorageKey(localStorageKey);
  const panelHistoryStorageKey = guidePanelHistoryStorageKey(localStorageKey);
  const navigationModal = useModal();
  const formNavigationGuard = useRef<GuideFormNavigationGuard | null>(null);

  const [index, setIndex] = useState<GuideIndex | null>(null);
  const [indexLoading, setIndexLoading] = useState(false);
  const [indexError, setIndexError] = useState(false);
  const indexPromise = useRef<Promise<GuideIndex | null> | null>(null);
  const guideCache = useRef(new Map<string, GuideDetail>());
  const tourCache = useRef(new Map<string, TourDefinition>());
  const pathCache = useRef(new Map<string, PathDefinition>());
  const panelRouteNavigation = useRef<{
    guideId: string;
    fromPathname: string;
  } | null>(null);

  const [panelGuideId, setPanelGuideId] = useState<string | null>(null);
  const [panelHistory, setPanelHistory] = useState<GuidePanelHistory>(() =>
    token && userLogin
      ? readGuidePanelHistory(panelHistoryStorageKey, userLogin)
      : EMPTY_GUIDE_PANEL_HISTORY
  );
  const [activeTour, setActiveTour] = useState<ActiveTour | null>(null);
  const [progress, setProgress] = useState<GuideProgressMap>({});

  const registerFormNavigationGuard = useCallback(
    (guard: GuideFormNavigationGuard) => {
      formNavigationGuard.current = guard;
      return () => {
        if (formNavigationGuard.current === guard) {
          formNavigationGuard.current = null;
        }
      };
    },
    []
  );

  // Definitions are per session/language: drop caches when the session changes.
  useEffect(() => {
    guideCache.current.clear();
    tourCache.current.clear();
    pathCache.current.clear();
    indexPromise.current = null;
    setIndex(null);
    setIndexError(false);
  }, [token]);

  useEffect(() => {
    setPanelHistory(
      token && userLogin
        ? readGuidePanelHistory(panelHistoryStorageKey, userLogin)
        : EMPTY_GUIDE_PANEL_HISTORY
    );
  }, [panelHistoryStorageKey, token, userLogin]);

  const updatePanelHistory = useCallback(
    (update: (current: GuidePanelHistory) => GuidePanelHistory) => {
      setPanelHistory((current) => {
        const next = update(current);
        writeGuidePanelHistory(panelHistoryStorageKey, userLogin, next);
        return next;
      });
    },
    [panelHistoryStorageKey, userLogin]
  );

  // Progress: local mirror first (instant UI), then server wins.
  useEffect(() => {
    if (!token || !userLogin) {
      setProgress({});
      return;
    }
    const local = readLocalGuideProgress(storageKey, userLogin);
    setProgress(local);
    let cancelled = false;
    requestGuideProgress(token)
      .then((response) => {
        if (cancelled) return;
        const merged = mergeGuideProgress(local, response.items);
        setProgress(merged);
        writeLocalGuideProgress(storageKey, userLogin, merged);
      })
      .catch(() => {
        // Offline or older backend: keep the local mirror.
      });
    return () => {
      cancelled = true;
    };
  }, [token, userLogin, storageKey]);

  const markProgress = useCallback<GuideContextValue['markProgress']>(
    (kind, itemId, status, step) => {
      setProgress((previous) => {
        const next = applyGuideProgress(previous, {
          kind,
          itemId,
          status,
          step,
        });
        writeLocalGuideProgress(storageKey, userLogin, next);
        return next;
      });
      if (!token) return;
      postGuideProgress(token, {
        kind,
        itemId,
        status,
        step: step ?? null,
      }).catch(() => {
        // Best effort; the local mirror keeps the state until the next successful sync.
      });
    },
    [storageKey, token, userLogin]
  );

  const ensureIndex = useCallback<
    GuideContextValue['ensureIndex']
  >(async () => {
    if (index) return index;
    if (!token) return null;
    if (!indexPromise.current) {
      setIndexLoading(true);
      setIndexError(false);
      indexPromise.current = requestGuideIndex(token)
        .then((loaded) => {
          setIndex(loaded);
          return loaded;
        })
        .catch(() => {
          setIndexError(true);
          indexPromise.current = null;
          return null;
        })
        .finally(() => setIndexLoading(false));
    }
    return indexPromise.current;
  }, [index, token]);

  const loadGuide = useCallback<GuideContextValue['loadGuide']>(
    async (guideId) => {
      const cached = guideCache.current.get(guideId);
      if (cached) return cached;
      if (!token) return null;
      try {
        const response = await requestGuide(guideId, token);
        guideCache.current.set(guideId, response.guide);
        return response.guide;
      } catch {
        return null;
      }
    },
    [token]
  );

  const loadTour = useCallback<GuideContextValue['loadTour']>(
    async (tourId) => {
      const cached = tourCache.current.get(tourId);
      if (cached) return cached;
      if (!token) return null;
      try {
        const response = await requestTour(tourId, token);
        tourCache.current.set(tourId, response.tour);
        return response.tour;
      } catch {
        return null;
      }
    },
    [token]
  );

  const loadPath = useCallback<GuideContextValue['loadPath']>(
    async (pathId) => {
      const cached = pathCache.current.get(pathId);
      if (cached) return cached;
      if (!token) return null;
      try {
        const response = await requestPath(pathId, token);
        pathCache.current.set(pathId, response.path);
        return response.path;
      } catch {
        return null;
      }
    },
    [token]
  );

  const currentScreenGuideId = useMemo(
    () => guideIdForPathname(location.pathname, routes.home()),
    [location.pathname, routes]
  );

  const openPanel = useCallback<GuideContextValue['openPanel']>(
    (guideId) => {
      void ensureIndex();
      setPanelGuideId(guideId);
      updatePanelHistory((current) => pushGuidePanelHistory(current, guideId));
      setProgress((previous) => {
        if (getGuideProgress(previous, 'guide', guideId)) return previous;
        const next = applyGuideProgress(previous, {
          kind: 'guide',
          itemId: guideId,
          status: 'seen',
        });
        writeLocalGuideProgress(storageKey, userLogin, next);
        if (token) {
          postGuideProgress(token, {
            kind: 'guide',
            itemId: guideId,
            status: 'seen',
            step: null,
          }).catch(noop);
        }
        return next;
      });
    },
    [ensureIndex, storageKey, token, updatePanelHistory, userLogin]
  );

  const navigateToUrl = useCallback<GuideContextValue['navigateToUrl']>(
    (targetUrl, afterNavigation, navigationState) => {
      const performNavigation = () => {
        afterNavigation?.();
        if (location.pathname !== targetUrl || navigationState !== undefined) {
          history.push(targetUrl, navigationState);
        }
      };
      const guard = formNavigationGuard.current;
      if (location.pathname !== targetUrl && guard?.hasUnsavedChanges) {
        navigationModal.open(
          <GuideNavigationModalContent
            onContinue={() => {
              navigationModal.close();
              performNavigation();
            }}
            onSave={() => {
              navigationModal.close();
              guard.save(performNavigation);
            }}
            onCancel={navigationModal.close}
          />
        );
        return false;
      }
      performNavigation();
      return true;
    },
    [history, location.pathname, navigationModal.close, navigationModal.open]
  );

  const navigate = useCallback<GuideContextValue['navigate']>(
    (action, afterNavigation) => {
      if (action.type !== 'navigate') return false;
      const { params } = action;
      let url: string | null = null;
      switch (action.route) {
        case 'datasetList':
          url = params.datasetId ? routes.datasetList(params.datasetId) : null;
          break;
        case 'formAddAction':
          url = params.formId ? routes.formAddAction(params.formId) : null;
          break;
        case 'formListAddAction':
          url =
            params.datasetId && params.formId
              ? routes.formListAddAction(params.datasetId, params.formId)
              : null;
          break;
        case 'application':
          url = params.appId ? routes.application(params.appId) : null;
          break;
        case 'dashboards':
          url = routes.dashboards();
          break;
        case 'guides':
          url = routes.guides();
          break;
        default:
          url = null;
      }
      if (!url) return false;
      return navigateToUrl(url, afterNavigation);
    },
    [navigateToUrl, routes]
  );

  const navigateToGuideScreen = useCallback(
    (guideId: string, afterNavigation: () => void): boolean => {
      const target = index?.guides.find((item) => item.id === guideId)?.target;
      if (!target) return false;
      const action = navigationForGuideTarget(target, false);
      if (!action) return false;
      navigate(action, () => {
        panelRouteNavigation.current = {
          guideId,
          fromPathname: location.pathname,
        };
        afterNavigation();
      });
      return true;
    },
    [index, location.pathname, navigate]
  );

  const openPanelAtScreen = useCallback<GuideContextValue['openPanelAtScreen']>(
    (guideId) => {
      if (!navigateToGuideScreen(guideId, () => openPanel(guideId))) {
        openPanel(guideId);
      }
    },
    [navigateToGuideScreen, openPanel]
  );

  const previousPanelGuideId = previousGuideId(panelHistory);
  const nextPanelGuideId = nextGuideId(panelHistory);

  useEffect(() => {
    if (!panelGuideId || !currentScreenGuideId || !index) return;
    const pendingNavigation = panelRouteNavigation.current;
    if (pendingNavigation) {
      if (currentScreenGuideId === pendingNavigation.guideId) {
        panelRouteNavigation.current = null;
        return;
      }
      if (location.pathname === pendingNavigation.fromPathname) return;
      panelRouteNavigation.current = null;
    }
    if (currentScreenGuideId === panelGuideId) return;
    if (!index.guides.some((guide) => guide.id === currentScreenGuideId))
      return;
    openPanel(currentScreenGuideId);
  }, [currentScreenGuideId, index, location.pathname, openPanel, panelGuideId]);

  const goBackPanel = useCallback(() => {
    if (!previousPanelGuideId) return;
    const updatePanel = () => {
      setPanelGuideId(previousPanelGuideId);
      updatePanelHistory((current) => moveGuidePanelHistoryBack(current));
    };
    if (!navigateToGuideScreen(previousPanelGuideId, updatePanel)) {
      updatePanel();
    }
  }, [navigateToGuideScreen, previousPanelGuideId, updatePanelHistory]);

  const goForwardPanel = useCallback(() => {
    if (!nextPanelGuideId) return;
    const updatePanel = () => {
      setPanelGuideId(nextPanelGuideId);
      updatePanelHistory((current) => moveGuidePanelHistoryForward(current));
    };
    if (!navigateToGuideScreen(nextPanelGuideId, updatePanel)) {
      updatePanel();
    }
  }, [navigateToGuideScreen, nextPanelGuideId, updatePanelHistory]);

  const closePanel = useCallback(() => {
    setPanelGuideId(null);
  }, []);

  const startTour = useCallback<GuideContextValue['startTour']>(
    async (tourId, mode = 'interactive') => {
      const tour = await loadTour(tourId);
      if (!tour || tour.steps.length === 0) return;
      closePanel();
      setActiveTour({ tour, stepIndex: 0, mode });
      markProgress('tour', tourId, 'started', 0);
    },
    [closePanel, loadTour, markProgress]
  );

  const stopTour = useCallback(() => setActiveTour(null), []);

  const nextStep = useCallback(() => {
    setActiveTour((current) => {
      if (!current) return current;
      const last = current.stepIndex >= current.tour.steps.length - 1;
      if (last) {
        markProgress(
          'tour',
          current.tour.id,
          'completed',
          current.tour.steps.length
        );
        return null;
      }
      const stepIndex = current.stepIndex + 1;
      markProgress('tour', current.tour.id, 'started', stepIndex);
      return { ...current, stepIndex };
    });
  }, [markProgress]);

  const prevStep = useCallback(() => {
    setActiveTour((current) =>
      current && current.stepIndex > 0
        ? { ...current, stepIndex: current.stepIndex - 1 }
        : current
    );
  }, []);

  const toggleMode = useCallback(() => {
    setActiveTour((current) =>
      current
        ? {
            ...current,
            mode: current.mode === 'autoplay' ? 'interactive' : 'autoplay',
          }
        : current
    );
  }, []);

  // Logging out ends any walkthrough and closes the panel.
  useEffect(() => {
    if (!token) {
      setActiveTour(null);
      setPanelGuideId(null);
      setPanelHistory(EMPTY_GUIDE_PANEL_HISTORY);
    }
  }, [token]);

  const value = useMemo<GuideContextValue>(
    () => ({
      isProvided: true,
      index,
      indexLoading,
      indexError,
      ensureIndex,
      loadGuide,
      loadTour,
      loadPath,
      currentScreenGuideId,
      panelGuideId,
      openPanel,
      openPanelAtScreen,
      closePanel,
      panelCanGoBack: !!previousPanelGuideId,
      panelCanGoForward: !!nextPanelGuideId,
      goBackPanel,
      goForwardPanel,
      activeTour,
      startTour,
      nextStep,
      prevStep,
      stopTour,
      toggleMode,
      progress,
      markProgress,
      navigate,
      navigateToUrl,
      registerFormNavigationGuard,
    }),
    [
      index,
      indexLoading,
      indexError,
      ensureIndex,
      loadGuide,
      loadTour,
      loadPath,
      currentScreenGuideId,
      panelGuideId,
      openPanel,
      openPanelAtScreen,
      closePanel,
      previousPanelGuideId,
      nextPanelGuideId,
      goBackPanel,
      goForwardPanel,
      activeTour,
      startTour,
      nextStep,
      prevStep,
      stopTour,
      toggleMode,
      progress,
      markProgress,
      navigate,
      navigateToUrl,
      registerFormNavigationGuard,
    ]
  );

  return (
    <GuideContext.Provider value={value}>
      {children}
      {navigationModal.isOpen && <Modal {...navigationModal.modalProps} />}
    </GuideContext.Provider>
  );
};

export default GuideContextProvider;
